import { useEffect, useState, useCallback } from 'react';
import { useStoreConfig } from './hooks/useStoreConfig';
import ErrorBoundary from './components/ErrorBoundary';
import ChatWindow from './components/ChatWindow';
import StorePage from './components/StorePage';
import ChatFab from './components/ChatFab';
import ChatDrawer from './components/ChatDrawer';
import { resolveWidgetConfig } from './widgetConfig';

interface Props {
  slug: string;
  /** Render flush inside an embedded panel (no outer page chrome). */
  embedded?: boolean;
  onClose?: () => void;
}

/** Apply store brand colors as CSS custom properties on the root element. */
function applyBrand(primary?: string, accent?: string) {
  const root = document.documentElement;
  if (accent) {
    root.style.setProperty('--brand-accent', accent);
  }
  if (primary) {
    root.style.setProperty('--brand-primary', primary);
  }
}

export default function App({ slug, embedded, onClose }: Props) {
  const { config, loading, error } = useStoreConfig(slug);
  const [chatOpen, setChatOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const closeChat = useCallback(() => setChatOpen(false), []);
  const widgetConfig = resolveWidgetConfig(config?.widgetConfig);

  useEffect(() => {
    // Embedded installs inherit variables from their shadow host so the widget
    // never writes generic brand variables onto the merchant's document root.
    if (config && !embedded) {
      applyBrand(config.brandColors?.primary, config.brandColors?.accent);
    }
  }, [config, embedded]);

  const handleAskAbout = useCallback(
    (message: string) => {
      setChatOpen(true);
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('cartiq:send-message', { detail: { message } }),
        );
      }, 400);
    },
    [],
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.cartCount !== undefined) {
        setCartCount(detail.cartCount);
      }
    };
    window.addEventListener('cartiq:cart-update', handler);
    return () => window.removeEventListener('cartiq:cart-update', handler);
  }, []);

  // Embedded widget keeps the narrow column dictated by its host panel.
  if (embedded) {
    return (
      <div className="h-full w-full bg-surface">
        {loading ? (
          <LoadingState />
        ) : error || !config ? (
          <ErrorState message={error ?? 'Store not found'} />
        ) : (
          <ErrorBoundary>
            <ChatWindow slug={slug} config={config} onClose={onClose} embedded />
          </ErrorBoundary>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      {loading ? (
        <LoadingState />
      ) : error || !config ? (
        <ErrorState message={error ?? 'Store not found'} />
      ) : (
        <ErrorBoundary>
          <StorePage slug={slug} config={config} onAskAbout={handleAskAbout} />

          <ChatFab
            cartCount={cartCount}
            hasCart={cartCount > 0}
            onClick={() => setChatOpen(true)}
            widgetConfig={widgetConfig}
          />

          <ChatDrawer
            slug={slug}
            config={config}
            open={chatOpen}
            onClose={closeChat}
            widgetConfig={widgetConfig}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-[var(--brand-accent)]" />
      <p className="text-xs text-mid">Loading store…</p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-surface p-6 text-center">
      <div className="text-2xl">🛍️</div>
      <p className="text-sm font-semibold text-primary">We couldn't load this store</p>
      <p className="max-w-xs text-xs text-mid">{message}</p>
    </div>
  );
}
